<script setup>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from "../../Shared/Tables/Pagination";

const props = defineProps({
    products: Object,
    categories: Object,
    suppliers: Object,
})

import { ref, watch } from 'vue'
import {
    Dialog,
    DialogOverlay,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'

import { ChevronDownIcon, FilterIcon, MinusSmIcon, PlusSmIcon, ViewGridIcon, StarIcon } from '@heroicons/vue/solid'
import {Inertia} from "@inertiajs/inertia";

const sortOptions = [
    { name: 'Populariteit', href: '#', current: true },
    { name: 'Beste Rating', href: '#', current: false },
    { name: 'Nieuwste', href: '#', current: false },
]
const subCategories = [
    { name: 'Tilliften', href: '#' },
    { name: 'Rolstoel', href: '#' },
    { name: 'AED', href: '#' },
]
const filters = [
    {
        id: 'category',
        name: 'Categorieën',
        options: props.categories,
    },
    {
        id: 'supplier',
        name: 'Leveranciers',
        options: props.suppliers,
    },
]

let selectedFilters = ref({});

const mobileFiltersOpen = ref(false)

// single ref
watch(selectedFilters, (newX) => {
  let tableRoute = route(`storefront`,  { _query: newX });
  Inertia.get(tableRoute, {}, {
    replace: true,
    preserveState: true
  });
}, { deep: true })
</script>


<template>
    <div class="bg-white">
        <div>
            <!-- Mobile filter dialog -->
            <TransitionRoot as="template" :show="mobileFiltersOpen">
                <Dialog as="div" class="fixed inset-0 flex z-40 lg:hidden" @close="mobileFiltersOpen = false">
                    <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
                        <DialogOverlay class="fixed inset-0 bg-black bg-opacity-25" />
                    </TransitionChild>

                    <TransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-12 flex flex-col overflow-y-auto">
                            <div class="px-4 flex items-center justify-between">
                                <h2 class="text-lg font-medium text-gray-900">Filters</h2>
                                <button type="button" class="-mr-2 w-10 h-10 bg-white p-2 rounded-md flex items-center justify-center text-gray-400" @click="mobileFiltersOpen = false">
                                    <span class="sr-only">Close menu</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <!-- Filters -->
                            <form class="mt-4 border-t border-gray-200">
                                <Disclosure as="div" defaultOpen v-for="section in filters" :key="section.id" class="border-t border-gray-200 px-4 py-6" v-slot="{ open }">
                                    <h3 class="-mx-2 -my-3 flow-root">
                                        <DisclosureButton class="px-2 py-3 bg-white w-full flex items-center justify-between text-gray-400 hover:text-gray-500">
                                          <span class="font-medium text-gray-900">
                                            {{ section.name }}
                                          </span>
                                          <span class="ml-6 flex items-center">
                                            <PlusSmIcon v-if="!open" class="h-5 w-5" aria-hidden="true" />
                                            <MinusSmIcon v-else class="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        </DisclosureButton>
                                    </h3>
                                    <DisclosurePanel class="pt-6">
                                        <div class="space-y-6">
                                          <div v-for="(option, optionIdx) in section.options" :key="option.value" class="flex items-center">
                                            <input :id="`filter-${section.id}-${optionIdx}`" :true-value="[option.value]" v-model="selectedFilters[section.id]" :value="option.value" type="checkbox" class="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary" />
                                            <label :for="`filter-${section.id}-${optionIdx}`" class="ml-3 text-sm text-gray-600">
                                              {{ option.label }}
                                            </label>
                                          </div>
                                        </div>
                                    </DisclosurePanel>
                                </Disclosure>
                            </form>
                        </div>
                    </TransitionChild>
                </Dialog>
            </TransitionRoot>

            <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="relative flex items-baseline justify-between pt-8 pb-6 border-b border-gray-200">
                    <h1 class="text-4xl font-extrabold tracking-tight text-gray-900">
                      Webshop
                    </h1>

                    <div class="flex items-center">
                        <Menu as="div" class="relative inline-block text-left">
                            <div>
                                <MenuButton class="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                    Sorteren
                                    <ChevronDownIcon class="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                                </MenuButton>
                            </div>

                            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div class="py-1">
                                        <MenuItem v-for="option in sortOptions" :key="option.name" v-slot="{ active }">
                                            <button @click="applySort" :class="[option.current ? 'font-medium text-gray-900' : 'text-gray-500', active ? 'bg-gray-100' : '', 'w-full block px-4 py-2 text-sm text-left']">
                                                {{ option.name }}
                                            </button>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </transition>
                        </Menu>

                        <button type="button" class="p-2 -m-2 ml-4 sm:ml-6 text-gray-400 hover:text-gray-500 lg:hidden" @click="mobileFiltersOpen = true">
                            <span class="sr-only">Filters</span>
                            <FilterIcon class="w-5 h-5" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <section aria-labelledby="products-heading" class="pt-6 pb-6">
                    <h2 id="products-heading" class="sr-only">Products</h2>

                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-x-8 gap-y-10">
                        <!-- Filters -->
                        <form class="hidden lg:block">
                            <Disclosure as="div" defaultOpen v-for="section in filters" :key="section.id" class="border-b border-gray-200 py-6" v-slot="{ open }">
                                <h3 class="-my-3 flow-root">
                                    <DisclosureButton class="py-3 bg-white w-full flex items-center justify-between text-sm text-gray-400 hover:text-gray-500">
                                    <span class="font-medium text-gray-900">
                                      {{ section.name }}
                                    </span>
                                      <span class="ml-6 flex items-center">
                                      <PlusSmIcon v-if="!open" class="h-5 w-5" aria-hidden="true" />
                                      <MinusSmIcon v-else class="h-5 w-5" aria-hidden="true" />
                                    </span>
                                    </DisclosureButton>
                                </h3>
                                <DisclosurePanel class="pt-6">
                                    <div class="space-y-4">
                                        <div v-for="(option, optionIdx) in section.options" :key="option.value" class="flex items-center">
                                            <input :id="`filter-${section.id}-${optionIdx}`" :true-value="[option.value]" v-model="selectedFilters[section.id]" :value="option.value" type="checkbox" class="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary" />
                                            <label :for="`filter-${section.id}-${optionIdx}`" class="ml-3 text-sm text-gray-600">
                                                {{ option.label }}
                                            </label>
                                        </div>
                                    </div>
                                </DisclosurePanel>
                            </Disclosure>
                        </form>

                        <!-- Product grid -->
                        <div class="lg:col-span-3">
                            <div class="bg-white">
                                <div class="max-w-2xl mx-auto pb-16 px-4 sm:pb-24 sm:px-6 lg:max-w-7xl lg:px-8">

                                    <div class="mt-8 grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
                                        <div class="flex-1" v-for="product in products.data" :key="product.id">
                                            <div class="relative h-full">
                                                <div class="relative w-full h-72 rounded-lg overflow-hidden">
                                                    <img :src="product.image" alt="product picture" class="w-full h-full object-center object-contain" />
                                                </div>
                                                <div class="pt-3 flex flex-col items-center">
                                                  <p class="sr-only">{{ parseInt(product.rating) }} van de 5 sterren</p>
                                                  <div class="flex items-center">
                                                    <StarIcon v-for="rating in [0, 1, 2, 3, 4]" :key="rating" :class="[parseInt(product.rating) > rating ? 'text-yellow-400' : 'text-gray-200', 'flex-shrink-0 h-5 w-5']" aria-hidden="true" />
                                                  </div>
                                                </div>
                                                <div class="relative mt-4">
                                                    <h3 class="text-sm font-medium text-gray-900">{{ product.name }}</h3>
                                                    <p class="mt-1 text-sm text-gray-500">{{ product.short_description }}</p>

                                                </div>
                                                <div class="absolute top-0 inset-x-0 h-72 rounded-lg p-4 flex items-end justify-end overflow-hidden">
                                                  <div aria-hidden="true" class="absolute inset-x-0 bottom-0 h-36" />
                                                  <p v-if="product.price !== '0.00'" class="relative text-lg font-semibold text-white">€{{ product.price }} p/d</p>
                                                </div>
                                            </div>
                                            <div class="">
                                                <Link :href="route('rentable.detail', product.slug)" class="relative flex bg-primary border border-transparent rounded-md py-2 px-8 items-center justify-center text-sm font-medium text-white hover:bg-gray-200">
                                                  Details <span class="sr-only">, {{ product.name }}</span>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <Pagination :meta="products.meta" :links="products.links" />
                            </div>
                            <!-- /End replace -->
                        </div>
                    </div>
                </section>
            </main>
        </div>
    </div>
</template>



